import React, { useState } from "react";
import CPFInput from "../../../components/CPFInput/CPFInput";
import { InputField, MaskedInputField, TextareaField } from "./InputFields";
import "./formStyles.scss";

// Função para remover caracteres não numéricos
const sanitizeNumber = (value) => {
  if (value && typeof value === "string") {
    return value.replace(/\D/g, ""); // Apenas sanitiza se o valor for uma string
  }
  return ""; // Retorna vazio se não for uma string válida
};

const StepThree = ({
  nome,
  setNome,
  email,
  setEmail,
  cpf,
  setCpf,
  dataNascimento,
  setDataNascimento,
  telefone,
  setTelefone,
  cep,
  setCep,
  logradouro,
  setLogradouro,
  bairro,
  setBairro,
  cidade,
  setCidade,
  estado,
  setEstado,
  numero,
  setNumero,
  mensagem,
  setMensagem,
  nextStep,
  prevStep,
  complemento,
  setComplemento,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  const handleCepBlur = async (e) => {
    const cepValue = sanitizeNumber(e.target.value);
    if (cepValue.length === 8) {
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cepValue}/json/`
        );
        const data = await response.json();
        if (!data.erro) {
          setLogradouro(data.logradouro);
          setBairro(data.bairro);
          setCidade(data.localidade);
          setEstado(data.uf);
        } else {
          setMessage("CEP não encontrado.");
        }
      } catch (error) {
        console.error("Erro ao buscar o CEP:", error);
        setMessage("Erro ao buscar o CEP.");
      }
    }
  };

  // Estado para validação do CPF
  const [cpfValidation, setCpfValidation] = useState({
    isValid: null,
    error: null,
    cleaned: "",
    formatted: "",
    isComplete: false,
  });

  // Função para validar o formulário
  const validateForm = () => {
    const newErrors = {};

    // Validações manuais
    if (!nome.trim()) newErrors.nome = "Nome é obrigatório";
    if (!email.includes("@")) newErrors.email = "Email inválido";

    // Validação do CPF usando o validador
    if (!cpfValidation.isValid) {
      newErrors.cpf = cpfValidation.error || "CPF inválido";
    }

    if (sanitizeNumber(telefone).length !== 11)
      newErrors.telefone = "Telefone inválido";
    if (sanitizeNumber(dataNascimento).length !== 8)
      newErrors.dataNascimento = "Data de nascimento inválida";
    if (sanitizeNumber(cep).length !== 8) newErrors.cep = "CEP inválido";
    if (!logradouro.trim()) newErrors.logradouro = "Logradouro é obrigatório";
    if (!bairro.trim()) newErrors.bairro = "Bairro é obrigatório";
    if (!cidade.trim()) newErrors.cidade = "Cidade é obrigatória";
    if (!estado.trim()) newErrors.estado = "Estado é obrigatório";
    if (!numero.trim()) newErrors.numero = "Número é obrigatório";

    setErrors(newErrors);

    // Se não houver erros, o formulário está válido
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // Se o formulário for inválido, interrompa o envio
    if (!validateForm()) {
      return;
    }

    const formData = {
      nome,
      email,
      cpf: cpfValidation.cleaned || sanitizeNumber(cpf),
      dataNascimento: sanitizeNumber(dataNascimento),
      telefone: sanitizeNumber(telefone),
      cep: sanitizeNumber(cep),
      logradouro,
      bairro,
      cidade,
      estado,
      numero,
      mensagem,
    };

    setIsSubmitting(true);

    console.log("Formulário enviado com os dados:", formData);

    // Simula o envio de dados com um delay
    setTimeout(() => {
      setIsSubmitting(false);
      setMessage("Formulário enviado com sucesso!");
      nextStep();
    }, 2000);
  };

  return (
    <>
      {isSubmitting ? (
        <div className="loader">Enviando...</div>
      ) : (
        <form onSubmit={onSubmit} className="row">
          {message && (
            <div className={`message ${isSubmitting ? "loading" : "success"}`}>
              {message}
            </div>
          )}

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Nome"
              name="nome"
              value={nome} // Valor vindo do estado
              onChange={(e) => setNome(e.target.value)} // Atualiza o estado
              error={errors.nome}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Email"
              name="email"
              type="email"
              value={email} // Valor vindo do estado
              onChange={(e) => setEmail(e.target.value)} // Atualiza o estado
              error={errors.email}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <CPFInput
              label="CPF"
              name="cpf"
              value={cpf}
              onChange={(value, validation) => {
                setCpf(value);
                setCpfValidation(validation);
              }}
              error={errors.cpf}
              required={true}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <MaskedInputField
              label="Data de Nascimento"
              name="dataNascimento"
              mask="99/99/9999"
              value={dataNascimento}
              onChange={(e) => setDataNascimento(e.target.value)}
              error={errors.dataNascimento}
              inputMode="numeric"
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <MaskedInputField
              label="Telefone"
              name="telefone"
              mask="(99) 99999-9999"
              value={telefone} // Valor vindo do estado
              onChange={(e) => setTelefone(e.target.value)} // Atualiza o estado
              error={errors.telefone}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <MaskedInputField
              label="CEP"
              name="cep"
              mask="99999-999"
              value={cep} // Valor vindo do estado
              onChange={(e) => setCep(e.target.value)} // Atualiza o estado
              onBlur={handleCepBlur}
              error={errors.cep}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Logradouro"
              name="logradouro"
              value={logradouro} // Valor vindo do estado
              onChange={(e) => setLogradouro(e.target.value)} // Atualiza o estado
              error={errors.logradouro}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Bairro"
              name="bairro"
              value={bairro} // Valor vindo do estado
              onChange={(e) => setBairro(e.target.value)} // Atualiza o estado
              error={errors.bairro}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Cidade"
              name="cidade"
              value={cidade} // Valor vindo do estado
              onChange={(e) => setCidade(e.target.value)} // Atualiza o estado
              error={errors.cidade}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Estado"
              name="estado"
              value={estado} // Valor vindo do estado
              onChange={(e) => setEstado(e.target.value)} // Atualiza o estado
              error={errors.estado}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Número"
              name="numero"
              value={numero} // Valor vindo do estado
              onChange={(e) => setNumero(e.target.value)} // Atualiza o estado
              error={errors.numero}
            />
          </div>

          <div className="col-xl-6 col-lg-6 col-md-6">
            <InputField
              label="Complemento"
              name="complemento"
              value={complemento}
              onChange={(e) => setComplemento(e.target.value)}
              placeholder="Apto, bloco, sala, etc."
            />
          </div>

          <div className="col-xl-12">
            <TextareaField
              label="Mensagem"
              name="mensagem"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
                placeholder="Caso deseje nos informar algo, fique à vontade."
              rows={4}
            />
          </div>

          <button className="primary_btn mt-40" type="submit">
            Próximo
          </button>
          <button className="secondary_btn mt-40" onClick={prevStep}>
            Anterior
          </button>
        </form>
      )}
    </>
  );
};

export default StepThree;
